import { countriesShopify, languagesEn } from '@shopexperts/shared';
import type { ExpertFilterSnapshot } from '@shopexperts/legacy-api';

/**
 * UI-side shape of the expert manager filters. Every scalar is a string so the
 * same object can round-trip through the URL query; `tagIds` and
 * `excludedTagIds` stay numeric because the API matches tags by ID.
 */
export interface ExpertManagerFilterState {
  search: string;
  status: string;
  expertType: string;
  country: string;
  serviceCategory: string;
  partnerTier: string;
  proStatus: '' | 'true' | 'false';
  language: string;
  claimStatus: '' | 'claimed' | 'unclaimed';
  tagIds: number[];
  excludedTagIds: number[];
}

export type ExpertManagerFilterKey = Exclude<keyof ExpertManagerFilterState, 'search' | 'tagIds' | 'excludedTagIds'>;

export const EXPERT_FILTER_QUERY_KEYS = [
  'search',
  'status',
  'expertType',
  'country',
  'serviceCategory',
  'partnerTier',
  'proStatus',
  'language',
  'claimStatus',
  'tags',
  'excludeTags',
] as const;

export function createEmptyExpertFilters(): ExpertManagerFilterState {
  return {
    search: '',
    status: '',
    expertType: '',
    country: '',
    serviceCategory: '',
    partnerTier: '',
    proStatus: '',
    language: '',
    claimStatus: '',
    tagIds: [],
    excludedTagIds: [],
  };
}

type QueryLike = Record<string, unknown>;

function readString(query: QueryLike, key: string, allowed?: readonly string[]): string {
  const value = query[key];
  if (typeof value !== 'string' || value === '') return '';
  // Guard enum-ish params so a hand-edited URL can't push a 422 to the API.
  if (allowed && !allowed.includes(value)) return '';
  return value;
}

function readIdList(query: QueryLike, key: string): number[] {
  const value = query[key];
  if (typeof value !== 'string' || value === '') return [];
  return [...new Set(value.split(',').map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))];
}

const sortIds = (ids: number[]) => [...ids].sort((a, b) => a - b);

export function expertFiltersFromQuery(query: QueryLike): ExpertManagerFilterState {

  return {
    search: readString(query, 'search'),
    status: readString(query, 'status', ['application', 'draft', 'published', 'unpublished', 'banned']),
    expertType: readString(query, 'expertType', ['freelancer', 'agency', 'app_service_provider']),
    country: readString(query, 'country'),
    serviceCategory: readString(query, 'serviceCategory'),
    partnerTier: readString(query, 'partnerTier', ['none', 'select', 'plus', 'premier', 'platinum']),
    proStatus: readString(query, 'proStatus', ['true', 'false']) as ExpertManagerFilterState['proStatus'],
    language: readString(query, 'language'),
    claimStatus: readString(query, 'claimStatus', ['claimed', 'unclaimed']) as ExpertManagerFilterState['claimStatus'],
    tagIds: readIdList(query, 'tags'),
    excludedTagIds: readIdList(query, 'excludeTags'),
  };
}

export function expertFiltersToQuery(filters: ExpertManagerFilterState): Record<string, string> {
  const query: Record<string, string> = {};
  if (filters.search) query.search = filters.search;
  if (filters.status) query.status = filters.status;
  if (filters.expertType) query.expertType = filters.expertType;
  if (filters.country) query.country = filters.country;
  if (filters.serviceCategory) query.serviceCategory = filters.serviceCategory;
  if (filters.partnerTier) query.partnerTier = filters.partnerTier;
  if (filters.proStatus) query.proStatus = filters.proStatus;
  if (filters.language) query.language = filters.language;
  if (filters.claimStatus) query.claimStatus = filters.claimStatus;
  if (filters.tagIds.length) query.tags = sortIds(filters.tagIds).join(',');
  if (filters.excludedTagIds.length) query.excludeTags = sortIds(filters.excludedTagIds).join(',');
  return query;
}

/**
 * Presets store the API-side snapshot, which rejects unknown keys and empty
 * strings, so only set keys that actually carry a value.
 */
export function expertFiltersToSnapshot(filters: ExpertManagerFilterState): ExpertFilterSnapshot {
  const snapshot: ExpertFilterSnapshot = {};
  if (filters.search) snapshot.search = filters.search;
  if (filters.status) snapshot.status = filters.status as ExpertFilterSnapshot['status'];
  if (filters.expertType) snapshot.expert_type = filters.expertType;
  if (filters.country) snapshot.country = filters.country;
  if (filters.serviceCategory) snapshot.service_category = filters.serviceCategory;
  if (filters.partnerTier) snapshot.partner_tier = filters.partnerTier;
  if (filters.proStatus) snapshot.is_pro = filters.proStatus === 'true';
  if (filters.language) snapshot.language = filters.language;
  if (filters.claimStatus) snapshot.claim_status = filters.claimStatus;
  if (filters.tagIds.length) snapshot.tag_ids = sortIds(filters.tagIds);
  if (filters.excludedTagIds.length) snapshot.exclude_tag_ids = sortIds(filters.excludedTagIds);
  return snapshot;
}

export function expertFiltersFromSnapshot(snapshot: ExpertFilterSnapshot | null | undefined): ExpertManagerFilterState {
  const filters = createEmptyExpertFilters();
  if (!snapshot) return filters;
  filters.search = snapshot.search ?? '';
  filters.status = snapshot.status ?? '';
  filters.expertType = snapshot.expert_type ?? '';
  filters.country = snapshot.country ?? '';
  filters.serviceCategory = snapshot.service_category ?? '';
  filters.partnerTier = snapshot.partner_tier ?? '';
  filters.proStatus = snapshot.is_pro === undefined ? '' : snapshot.is_pro ? 'true' : 'false';
  filters.language = snapshot.language ?? '';
  filters.claimStatus = snapshot.claim_status ?? '';
  filters.tagIds = sortIds(snapshot.tag_ids ?? []);
  filters.excludedTagIds = sortIds(snapshot.exclude_tag_ids ?? []);
  return filters;
}

export function expertFiltersEqual(a: ExpertManagerFilterState, b: ExpertManagerFilterState): boolean {
  const aQuery = expertFiltersToQuery(a);
  const bQuery = expertFiltersToQuery(b);
  const aKeys = Object.keys(aQuery);
  if (aKeys.length !== Object.keys(bQuery).length) return false;
  return aKeys.every((key) => aQuery[key] === bQuery[key]);
}

export function hasAnyExpertFilter(filters: ExpertManagerFilterState): boolean {
  return Object.keys(expertFiltersToQuery(filters)).length > 0;
}

/** Filters shown as removable chips — `search` has its own input, so it is excluded. */
export function countActiveExpertFilters(filters: ExpertManagerFilterState): number {
  const query = expertFiltersToQuery(filters);
  delete query.search;
  delete query.tags;
  delete query.excludeTags;
  return Object.keys(query).length + filters.tagIds.length + filters.excludedTagIds.length;
}

const LABEL_OVERRIDES: Record<string, string> = {
  app_service_provider: 'App Service',
};

export function formatFilterLabel(value: string): string {
  if (LABEL_OVERRIDES[value]) return LABEL_OVERRIDES[value];
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
}

const countryNameByCode = new Map<string, string>();
for (const country of countriesShopify) {
  countryNameByCode.set(country.iso2, country.name);
  countryNameByCode.set(country.name, country.name);
}

export function resolveCountryName(value: string): string {
  return countryNameByCode.get(value) ?? value;
}

// The API stores languages as ISO codes ("en", "fr"), which read as noise in a filter list.
const languageNameByCode = new Map<string, string>();
for (const language of languagesEn) {
  languageNameByCode.set(language.code, language.name);
  languageNameByCode.set(language.name, language.name);
}

export function resolveLanguageName(value: string): string {
  return languageNameByCode.get(value) ?? value;
}

export const STATUS_DOT_COLORS: Record<string, string> = {
  published: 'bg-emerald-400',
  application: 'bg-amber-400',
  draft: 'bg-gray-300',
  unpublished: 'bg-rose-400',
  banned: 'bg-gray-700',
};

export interface ActiveExpertFilter {
  /** Identifies what to clear: a scalar filter key, or a single included/excluded tag. */
  key: ExpertManagerFilterKey | `tag:${number}` | `excludeTag:${number}`;
  label: string;
  color?: string;
}

export function describeActiveExpertFilters(
  filters: ExpertManagerFilterState,
  tagNameById: Map<number, string> = new Map(),
): ActiveExpertFilter[] {
  const active: ActiveExpertFilter[] = [];
  if (filters.status) active.push({ key: 'status', label: formatFilterLabel(filters.status), color: STATUS_DOT_COLORS[filters.status] });
  if (filters.expertType) active.push({ key: 'expertType', label: formatFilterLabel(filters.expertType) });
  if (filters.country) active.push({ key: 'country', label: resolveCountryName(filters.country) });
  if (filters.serviceCategory) active.push({ key: 'serviceCategory', label: filters.serviceCategory });
  if (filters.partnerTier) active.push({ key: 'partnerTier', label: formatFilterLabel(filters.partnerTier) });
  if (filters.proStatus) active.push({ key: 'proStatus', label: filters.proStatus === 'true' ? 'Pro' : 'Non-pro' });
  if (filters.language) active.push({ key: 'language', label: resolveLanguageName(filters.language) });
  if (filters.claimStatus) active.push({ key: 'claimStatus', label: filters.claimStatus === 'claimed' ? 'Claimed' : 'Unclaimed' });
  for (const tagId of filters.tagIds) {
    active.push({ key: `tag:${tagId}`, label: tagNameById.get(tagId) ?? `Tag #${tagId}` });
  }
  for (const tagId of filters.excludedTagIds) {
    active.push({ key: `excludeTag:${tagId}`, label: `Not ${tagNameById.get(tagId) ?? `Tag #${tagId}`}` });
  }
  return active;
}

export function removeExpertFilter(filters: ExpertManagerFilterState, key: ActiveExpertFilter['key']): ExpertManagerFilterState {
  if (typeof key === 'string' && key.startsWith('tag:')) {
    const tagId = Number(key.slice(4));
    return { ...filters, tagIds: filters.tagIds.filter((id) => id !== tagId) };
  }
  if (typeof key === 'string' && key.startsWith('excludeTag:')) {
    const tagId = Number(key.slice(11));
    return { ...filters, excludedTagIds: filters.excludedTagIds.filter((id) => id !== tagId) };
  }
  return { ...filters, [key as ExpertManagerFilterKey]: '' };
}
